/** @jsx jsx */
import type { InputHTMLAttributes, ReactNode } from 'react';
import React, { forwardRef } from 'react';
import { jsx } from '@balance-web/core';
import { VisuallyHidden } from '@balance-web/a11y';
import { theme } from '@balance-web/theme';

import { useButtonBorderRadius } from './styles';

type RadioButtonProps = {
  children: ReactNode;
  checked: boolean;
  index: number;
  optionCount: number;
} & InputHTMLAttributes<HTMLInputElement>;

export const RadioButton = forwardRef<HTMLLabelElement, RadioButtonProps>(
  ({ children, optionCount, ...props }, ref) => {
    const indexToBorderRadius = useButtonBorderRadius({
      index: props.index,
      optionCount,
    });

    return (
      <label
        ref={ref}
        css={{
          alignItems: 'center',
          justifyContent: 'center',
          appearance: 'none',
          backgroundClip: 'padding-box',
          borderRadius: indexToBorderRadius,
          boxSizing: 'border-box',
          color: theme.semantic.colour.text.textPrimary,
          cursor: 'pointer',
          display: 'inline-flex',
          whiteSpace: 'nowrap',
          flex: 1,
          fontSize: theme.typography.fontSize.medium,
          outline: 0,
          paddingBottom: 0,
          paddingLeft: theme.spacing.medium,
          paddingRight: theme.spacing.medium,
          paddingTop: 0,
          position: 'relative',
          userSelect: 'none',
          zIndex: 2,
          transitionDuration: '250ms',
          transitionProperty: 'color',
          transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
          '&:hover': {
            background:
              theme.semantic.colour.primary.inverse.interactions.focused,
          },
          '&[data-checked="true"]': {
            color: theme.semantic.colour.surface.neutral.background,
          },
        }}
        data-checked={props.checked}
      >
        <VisuallyHidden as="input" type="radio" {...props} />
        {children}
      </label>
    );
  }
);
RadioButton.displayName = 'RadioButtonGroupSegment';
